/**
 * Self-contained fixtures for the **Neobank scenario** program-builder doc
 * blocks — a neobank configuring TWO programs in PMP end-to-end, then the
 * Programs page showing both Active.
 *
 * These reuse the same dependency-free shapes the other program doc blocks use
 * ({@link ProgramConfig}, {@link ConfigurablePolicy}, the tranche/wallet value
 * codecs, and {@link FundingAccount}) so the Neobank sims render the REAL
 * props-only Views with no wallet / RPC / orgApiFetch. Nothing here reaches an
 * app module.
 *
 * The two scenarios (from the neobank business case):
 *
 *  A — "Continuous Balance Rewards": ongoing rewards for holding USDv. A
 *      **Continuous** program (no fixed end) distributing every **14 days**,
 *      earned/variable budget. Eligibility = a **Minimum Threshold** of 100
 *      USDv; balance tiers (>1,000 / >10,000 / >50,000 USDv earn a higher
 *      allocation) mapped to a **Balance Amount Tranche** policy; a
 *      KYC-approved requirement mapped to **Specific Access Control** in
 *      **KYC Only** mode (only KYC-approved wallets qualify) — a real control.
 *
 *  B — "Deposit Campaign": a 90-day growth campaign. A **Fixed Length**
 *      (90-day), **Fixed Budget** (250,000 USDv) program funded from the
 *      rewards wallet. Eligibility = a **Minimum Threshold** of 250 USDv (the
 *      minimum deposit) + a **New User** policy — a purpose-built signup-bonus
 *      policy (one-time USDv bonus for customers who joined within a recency
 *      window) that replaces the former Specific-Access-Control-whitelist +
 *      Wallet-Activation pairing. It fully describes the signup reward on its
 *      own (bonus amount + eligibility window).
 *
 * GAPS / APPROXIMATIONS (documented so the AM review can decide):
 *  - **Funding** (Earnings vs External) — no longer a stored choice or a
 *    separate section: it's DERIVED from the Budget Type and shown as a
 *    read-only Configuration "Funding" field. Sim A (Continuous, Variable
 *    earned budget) reads "Any rewards generated are used for distributions.";
 *    Sim B (Fixed Budget) reads the External line ("You send the amount to
 *    distribute to <address>."). There is NO dedicated org-API funding field,
 *    so nothing funding-related is synced: the `client-added-funding-sources`
 *    required policy stays hardcoded `allowed: true` (required-policy contract
 *    kept intact). See the NOTE in `useDraftProgram.toBuilderConfig` /
 *    `useActivateClientProgram`.
 *  - **Revenue split** (80% customer / 20% neobank) — now driven by the
 *    Configuration **User %** field (Sim A sets it to 80). Backend-wise this
 *    maps to the `rewards-model` policy's `rewardsPct` (share to users); the
 *    dedicated backend field is still an approximation — see NOTE in
 *    `useDraftProgram.toBuilderConfig` / `useActivateClientProgram`.
 *  - **KYC-approved requirement** (Sim A) — a real **KYC Only** mode on
 *    **Specific Access Control**'s 3-way toggle (Whitelist / Blacklist / KYC
 *    Only; Sim A selects KYC Only — only KYC-approved wallets qualify). The
 *    mode rides inside the wallet policy's JSON value (`{ mode, addresses }`,
 *    `mode: "kyc-only"`), which has no dedicated backend sync path yet (same
 *    gap as the manual wallet list) — see NOTE below.
 *  - **One-time signup bonus** (Sim B) — now a real **New User** policy: a
 *    two-field signup-bonus policy (bonus amount + "joined within N days")
 *    whose value is JSON `{ bonusAmount, joinedWithinDays }`. This is a real
 *    catalog policy + config UI, no longer an approximation at the UI layer.
 *    TWO backend traits remain approximations, flagged here:
 *      • **Consume-once semantics** — once a user receives the bonus they are
 *        permanently ineligible. This is a stateful, per-user, BACKEND-enforced
 *        trait the policy implies (copy/behavior), NOT a UI control — there is
 *        deliberately no toggle for it.
 *      • **Join-date enforcement** — "joined within N days" requires the
 *        backend to know each user's join date and evaluate it at reward time;
 *        the UI only captures the window.
 *    Both ride inside the New User policy's JSON value, which — like Specific
 *    Access Control's wallet list — is a CUSTOM eligibility policy with no
 *    org-API sync path today (only the 4 required policies — `program-duration`
 *    / `rewards-model` / `settlement-cycle` / `client-added-funding-sources` —
 *    are synced in `useActivateClientProgram.syncClientRequiredProgramConfiguration`).
 *    So the New User value is unsynced, same pre-existing gap as the wallet
 *    list — see NOTE below.
 */

import type { FundingAccount } from "./fundingFixtures";
import type { NextDistribution } from "./NextDistributionMeter";
import type { Participant } from "./ParticipantRow";
import { serializeNewUserConfig, serializeTranches } from "./policyFixtures";
import { POLICY_CATALOG } from "./policyFixtures";
import type { HeaderTag } from "./ProgramActiveHeader";
import type { KeyStat } from "./ProgramKeyStats";
import type { ConfigurablePolicy, NewUserPolicy, Policy, ProgramConfig } from "./types";

// ── Catalog handles (typed lookups off the shared POLICY_CATALOG) ──────────
export const NB_MINIMUM_THRESHOLD = POLICY_CATALOG.find((p) => p.id === "minimum-threshold") as ConfigurablePolicy;
export const NB_BALANCE_AMOUNT_TRANCHE = POLICY_CATALOG.find(
    (p) => p.id === "balance-amount-tranche"
) as ConfigurablePolicy;
export const NB_WALLET_LIST = POLICY_CATALOG.find((p) => p.id === "wallet-list") as ConfigurablePolicy;
/** New User signup-bonus policy — Sim B's one-time deposit reward. */
export const NB_NEW_USER = POLICY_CATALOG.find((p) => p.id === "new-user") as NewUserPolicy;

// ─────────────────────────────────────────────────────────────────────────
// Sim A — "Continuous Balance Rewards"
// ─────────────────────────────────────────────────────────────────────────

export const NB_A_PROGRAM_NAME = "Continuous Balance Rewards";

/** The Continuous config Sim A fills in: Continuous program type (no fixed
 *  period), earned/variable budget, distribution every 14 days. */
export const NB_A_CONFIG = {
    programType: "continuous" as const,
    budgetMode: "open" as const,
    apyMode: "earned" as const,
    baseApy: 0,
    // Funding is DERIVED (Variable earned budget → "Any rewards generated are
    // used for distributions."), shown as a read-only Configuration field — no
    // stored fundingMode. See the FUNDING gap note above.
    /** Revenue split: 80% of earnings to customers (20% kept by the neobank)
     *  — the 80/20 split from the business case. */
    userPct: 80,
    /** No fixed end — Continuous. periodDays stays 0. */
    periodDays: 0,
    /** Distribute every 14 days. */
    settlementCycleLength: 14,
    settlementCycleUnit: "days" as const,
    budget: 0
};

/** Sim A eligibility floor: hold at least 100 USDv to qualify. */
export const NB_A_MINIMUM_THRESHOLD_VALUE = 100;

/** Sim A balance tiers → Balance Amount Tranche. Higher balances earn a higher
 *  reward multiplier (base 1×, then >1,000 / >10,000 / >50,000 USDv). */
export const NB_A_BALANCE_TRANCHES = [
    { amount: 0, multiplier: 1 },
    { amount: 1000, multiplier: 1.25 },
    { amount: 10000, multiplier: 1.5 },
    { amount: 50000, multiplier: 2 }
];
export const NB_A_BALANCE_TRANCHE_VALUE = serializeTranches(NB_A_BALANCE_TRANCHES);

// ── Sim A read view — the created program's active read composition ────────
// The settled active read view of the Continuous program — the RIGHT detail
// pane the outcome page shows for the selected "Continuous Balance Rewards"
// row. These fixtures back that read view: the active-header, key stats,
// progress meters (Continuous → none), read-only Configuration (from
// NB_A_CONFIG), read-only Policies (the three the sim built), and a small
// Qualified Users table.

/** Read-only Configuration program for Sim A — a `ProgramConfig` with `status:
 *  "active"` so `ProgramConfiguration` renders its read-summary path, carrying
 *  the Continuous / Variable-earned / 80% / 14-day values the sim built. */
export const NB_A_VIEW_CONFIG: ProgramConfig = {
    status: "active",
    programType: NB_A_CONFIG.programType,
    budgetMode: NB_A_CONFIG.budgetMode,
    baseApy: NB_A_CONFIG.baseApy,
    userPct: NB_A_CONFIG.userPct,
    period: "continuous",
    periodDays: NB_A_CONFIG.periodDays,
    settlementCycleLength: NB_A_CONFIG.settlementCycleLength,
    settlementCycleUnit: NB_A_CONFIG.settlementCycleUnit
};

/** Active-header tag chips — target · region · funding (Variable earned budget
 *  funds distributions from generated rewards). */
export const NB_A_VIEW_TAGS: HeaderTag[] = [
    { label: "Users", tone: "septenary" },
    { label: "Global", tone: "default" },
    { label: "Earned Rewards", tone: "sand" }
];

/** Live next-distribution cycle — 14-day cadence (Sim A's Distribution
 *  Schedule); `inSeconds` gives the countdown a running target. */
export const NB_A_VIEW_NEXT_DISTRIBUTION: NextDistribution = {
    accrued: 18400,
    projected: 26000,
    cycleDays: 14,
    inSeconds: 907200
};

/** Key-stats row for the active Continuous program. */
export const NB_A_VIEW_KEY_STATS: KeyStat[] = [
    { label: "Total USDv Held", value: "$1,240,000.00" },
    { label: "USDv Accrued", value: "$18,400.00" },
    { label: "USDv Projected", value: "$26,000.00" },
    { label: "USDv Distributed", value: "$142,000.00" },
    { label: "Users", value: "3,420" }
];

/** The three policies Sim A built, in build order — Minimum Threshold, Balance
 *  Amount Tranche, Specific Access Control (KYC Only). */
export const NB_A_VIEW_POLICIES: Policy[] = [
    NB_MINIMUM_THRESHOLD as Policy,
    NB_BALANCE_AMOUNT_TRANCHE as Policy,
    NB_WALLET_LIST as Policy
];

/** Configured values for the read-only Policies list, keyed by policy id — the
 *  100-USDv floor, the balance tranche ladder, and the KYC-Only wallet config. */
export const NB_A_VIEW_POLICY_VALUES: Record<string, number | string> = {
    "minimum-threshold": NB_A_MINIMUM_THRESHOLD_VALUE,
    "balance-amount-tranche": NB_A_BALANCE_TRANCHE_VALUE,
    "wallet-list": JSON.stringify({ mode: "kyc-only", addresses: [] })
};

/** A handful of qualified-user rows for the active read view. */
export const NB_A_VIEW_PARTICIPANTS: Participant[] = [
    {
        id: "nba-p1",
        name: "0x7c…3d90 · Aisha Bello",
        eligibility: "threshold_met",
        balance: 62000,
        earned: 1840.25,
        pending: 88.4,
        onNavigate: (): void => {}
    },
    {
        id: "nba-p2",
        name: "0x1f…8b22 · Wei Chen",
        eligibility: "threshold_met",
        balance: 24500,
        earned: 720.6,
        pending: 34.1,
        onNavigate: (): void => {}
    },
    {
        id: "nba-p3",
        name: "0x4a…5e17 · Diego Ramos",
        eligibility: "below_threshold",
        balance: 80,
        earned: 0,
        pending: 0,
        onNavigate: (): void => {}
    }
];

// ─────────────────────────────────────────────────────────────────────────
// Sim B — "Deposit Campaign"
// ─────────────────────────────────────────────────────────────────────────

export const NB_B_PROGRAM_NAME = "Deposit Campaign";

/** The 90-day Fixed-Length / Fixed-Budget config Sim B fills in. */
export const NB_B_CONFIG = {
    programType: "fixed" as const,
    budgetMode: "fixed" as const,
    apyMode: "earned" as const,
    baseApy: 0,
    // Funding is DERIVED (Fixed Budget → the External line, "You send the
    // amount to distribute to <address>."), shown as a read-only Configuration
    // field — no stored fundingMode. See the FUNDING gap note above.
    /** 90-day campaign. */
    periodDays: 90,
    /** Distribute weekly (7 days). */
    settlementCycleLength: 7,
    settlementCycleUnit: "days" as const,
    /** Fixed rewards budget funded from the rewards wallet. */
    budget: 250000
};

/** Sim B minimum deposit to qualify: 250 USDv. */
export const NB_B_MINIMUM_THRESHOLD_VALUE = 250;

/** Sim B one-time signup bonus → New User: 25 USDv for customers who joined
 *  within the last 30 days. Serialized to the `{ bonusAmount, joinedWithinDays }`
 *  JSON the new-user config codec reads/writes. */
export const NB_B_NEW_USER_BONUS_AMOUNT = 25;
export const NB_B_NEW_USER_JOINED_WITHIN_DAYS = 30;
export const NB_B_NEW_USER_VALUE = serializeNewUserConfig({
    bonusAmount: NB_B_NEW_USER_BONUS_AMOUNT,
    joinedWithinDays: NB_B_NEW_USER_JOINED_WITHIN_DAYS
});

// ── Sim B funding — the rewards wallet as the single funding source ────────
/** The rewards wallet the Deposit Campaign draws its fixed budget from. */
export const NB_REWARDS_WALLET: FundingAccount = {
    id: "neobank-rewards-wallet",
    name: "Rewards Wallet",
    reference: "0x6d21…9f8c · USDv",
    programIds: [],
    balance: 500000
};

// ─────────────────────────────────────────────────────────────────────────
// Outcome — the Programs page: both scenarios Active
// ─────────────────────────────────────────────────────────────────────────

/** A settled program row for the outcome list. Structural subset of the app's
 *  `Program` — only the fields the list row renders (id / name / status /
 *  region). `status` drives the dot; both rows are Active here. */
export interface NeobankProgramRow {
    id: string;
    name: string;
    status: "active" | "in_progress" | "ended";
    /** Region label shown on the row sub-line. */
    regionLabel: string;
}

/** The two programs the neobank just configured, both Active — the outcome. */
export const NB_ACTIVE_PROGRAMS: NeobankProgramRow[] = [
    {
        id: "neobank-continuous-balance-rewards",
        name: NB_A_PROGRAM_NAME,
        status: "active",
        regionLabel: "Global"
    },
    {
        id: "neobank-deposit-campaign",
        name: NB_B_PROGRAM_NAME,
        status: "active",
        regionLabel: "Global"
    }
];
